<script lang="ts">
import {computed, defineComponent, onMounted, ref, watch} from '@vue/composition-api'
import {DateTime} from 'luxon'

import coreApi from '@/api/core.api'
import ridoApi from '@/api/rido.api'
import CardWrapper from '@/components/general/CardWrapper.vue'
import ExpansionPanel from '@/components/general/ExpansionPanel.vue'
import LocationFormGroup from '@/components/repairshop/LocationFormGroup.vue'
import SalesAppointmentSettings from '@/components/repairshop/SalesAppointmentSettings.vue'
import {inRange, isEmail, isEmpty, isRequired} from '@/helpers/validations'
import {useI18n} from '@/i18n'
import router from '@/router'
import {DealerUser, DealerUserRole} from '@/types'
import {mapGetters, mapState} from '@/utils/map-store'
import {hasWeekdayOverlap} from '@/utils/opening-hours'

export default defineComponent({
  name: 'RepairShopProfile',
  components: {
    CardWrapper,
    ExpansionPanel,
    LocationFormGroup,
    SalesAppointmentSettings,
  },
  setup(props, {root, refs}) {
    const scope = 'honda.repair.repairshop'
    const {getLocale: locale, getDealerId} = mapGetters(root.$store.getters)
    const {permissions, dealerMid} = mapState(root.$store.state)
    const {t, i18n} = useI18n(scope)

    const panels = ref([0])
    const loading = ref(false)

    const dealer = ref()
    const holidays = ref([])
    const dealerUsers = ref<DealerUser[]>([])

    onMounted(async () => {
      loading.value = true

      const usersFetch =
        permissions.value.salesAppointments.canSee && getDealerId.value
          ? ridoApi.listDealerUsers(getDealerId.value).catch(error => {
              console.error('Error fetching dealer users', error)
              return undefined
            })
          : Promise.resolve(undefined)

      try {
        const [dealerResponse, usersResponse] = await Promise.all([coreApi.getDealer(dealerMid.value), usersFetch])
        dealer.value = dealerResponse.data
        if (usersResponse) {
          dealerUsers.value = (usersResponse.data?.data ?? []).filter(
            u => u.role === DealerUserRole.SalesAgent || u.role === DealerUserRole.DealerAdmin,
          )
        }

        if (!dealer.value.profile?.appointmentConditions?.appointmentsCapacityPerSlot) {
          dealer.value.profile.appointmentConditions.appointmentsCapacityPerSlot = 3
        }

        if (!dealer.value.profile.salesAppointmentConditions) {
          dealer.value.profile.salesAppointmentConditions = {}
        }
        if (!dealer.value.profile.salesAppointmentConditions.daysBeforeRequest) {
          dealer.value.profile.salesAppointmentConditions.daysBeforeRequest = 1
        }
        if (!dealer.value.profile.salesAppointmentConditions.capacityPerSlot) {
          dealer.value.profile.salesAppointmentConditions.capacityPerSlot = 4
        }
        if (!dealer.value.profile.salesAppointmentConditions.openingHours) {
          dealer.value.profile.salesAppointmentConditions.openingHours = dealer.value.profile.shop?.openingHours || []
        }
        if (
          !dealer.value.profile.salesAppointmentConditions.notificationEmail &&
          dealer.value.profile.hondaMapitAccount
        ) {
          dealer.value.profile.salesAppointmentConditions.notificationEmail = dealer.value.profile.hondaMapitAccount
        }

        holidays.value = dealer.value.profile?.appointmentConditions?.holidays?.map((date: string) => date)
      } catch (error: any) {
        console.error(error)

        if (error.response.status === 401) {
          logout()
        }
      }

      loading.value = false
    })

    const rules = computed(() => {
      const marginDays = 45
      const today = DateTime.now()

      return {
        email: [isEmpty, isEmail],
        marginDays: [isRequired, (value: number) => inRange(value, marginDays)],
        appointmentsCapacity: [isRequired, (value: number) => inRange(value, 20, 1)],
        name: [isRequired],
        dates: {
          min: today.toISO(),
          max: today.plus({year: 1}).toISO(),
        },
      }
    })

    function allowedDates(date: string) {
      const day = new Date(date).getDay()
      const excludeSundays = day !== 0
      const excludeSaturdays = day !== 6

      const {garage} = dealer.value.profile
      const openSaturdays = garage?.openingHours?.length === 3

      if (openSaturdays) {
        return excludeSundays
      }

      return excludeSaturdays && excludeSundays
    }

    function handleClose() {
      router.push({name: 'Dashboard'})
    }

    // Vuetify v-expansion-panel expand animation is ~300ms; wait a bit longer
    // before scrolling so the target field has settled at its final position.
    const EXPANSION_ANIMATION_MS = 350

    const OVERLAP_PANEL_PRIORITY: Array<{key: string; getOpeningHours: () => string[] | undefined}> = [
      {key: 'ep-garage-panel', getOpeningHours: () => dealer.value?.profile?.garage?.openingHours},
      {key: 'ep-shop-panel', getOpeningHours: () => dealer.value?.profile?.shop?.openingHours},
      {
        key: 'ep-sales-appointment-panel',
        getOpeningHours: () => dealer.value?.profile?.salesAppointmentConditions?.openingHours,
      },
    ]

    const SECTION_TITLE_KEY: Record<string, string> = {
      'ep-garage-panel': 'garage-data',
      'ep-shop-panel': 'shop-data',
      'ep-sales-appointment-panel': 'sales-appointment-data',
    }

    // Indices match the order of <expansion-panel> entries in the template:
    //   0 = General (always)
    //   1 = Shop (always)
    //   2 = SalesAppointment (only when permissions.salesAppointments.canSee)
    //   3 (or 2) = Garage
    //   4 (or 3) = Appointment (only when permissions.appointments.canSee)
    // If the template panel order changes, update this mapping.
    function panelIndexFor(target: string): number {
      const hasSales = permissions.value.salesAppointments.canSee
      switch (target) {
        case 'ep-shop-panel':
          return 1
        case 'ep-sales-appointment-panel':
          return hasSales ? 2 : -1
        case 'ep-garage-panel':
          return hasSales ? 3 : 2
      }
      return -1
    }

    async function handleSubmit() {
      loading.value = true

      // @ts-ignore
      const isValid = await refs.dealerContactForm.validate()

      // `dealer.value.profile` is mutated in place by the v-model chain
      // (LocationFormGroup → OpeningHoursSchedule), so reading it here gives
      // the live form state, not the last-fetched server snapshot.
      const overlapPanel = OVERLAP_PANEL_PRIORITY.find(p => hasWeekdayOverlap(p.getOpeningHours()))?.key ?? null

      if (!isValid || overlapPanel) {
        if (overlapPanel) {
          const idx = panelIndexFor(overlapPanel)
          if (idx >= 0 && !panels.value.includes(idx)) {
            panels.value = [...panels.value, idx]
          }
          setTimeout(() => {
            const el = document.querySelector(`[data-cy="${overlapPanel}"] #afternoon-hours`) as HTMLElement | null
            el?.scrollIntoView({behavior: 'smooth', block: 'center'})
          }, EXPANSION_ANIMATION_MS)
          const message = `${t(SECTION_TITLE_KEY[overlapPanel])}: ${i18n.t('validations.messages.afterTime')}`
          await root.$store.dispatch('showAlertError', message)
        }
        loading.value = false
        return
      }

      const payload = {
        name: dealer.value.name,
        profile: dealer.value.profile,
      }

      try {
        await coreApi.updateDealer(dealerMid.value, payload)
        await root.$store.dispatch('showAlertSuccess', 'honda.repair.updated-data')
      } catch (error: any) {
        console.error(error)

        if (error.response.status === 401) {
          logout()
          return
        }
      }

      loading.value = false
    }

    function logout() {
      loading.value = false
      root.$store.dispatch('logout')
      router.push({name: 'Login'})
    }

    watch(holidays, newHolidays => {
      dealer.value.profile.appointmentConditions.holidays = newHolidays.map((date: string) => date)
    })

    return {
      allowedDates,
      dealer,
      dealerUsers,
      handleClose,
      handleSubmit,
      holidays,
      loading,
      locale,
      panels,
      permissions,
      rules,
      t,
    }
  },
})
</script>

<template>
  <div>
    <card-wrapper :title="t('dealer-data')" id="dealer-profile">
      <v-form class="form" ref="dealerContactForm" @submit.prevent>
        <div v-if="dealer" class="animated fadeIn">
          <v-expansion-panels v-model="panels" multiple flat>
            <expansion-panel uid="ep-avatar" title="General" :hide-divider="true">
              <m-form-row :label="t('dealer-name')">
                <m-text-field
                  :placeholder="t('dealer-name')"
                  :rules="rules.name"
                  maxLength="100"
                  uid="dealer-name"
                  data-cy="dealer-name"
                  v-model="dealer.name" />
              </m-form-row>

              <m-form-row :label="t('contact-name')">
                <m-text-field
                  :placeholder="t('contact-name')"
                  :rules="rules.name"
                  maxLength="100"
                  uid="contact-name"
                  data-cy="contact-name"
                  v-model="dealer.profile.contactPoint.name" />
              </m-form-row>

              <m-form-row :label="t('contact-email')">
                <m-text-field
                  :placeholder="t('contact-email')"
                  :rules="rules.email"
                  maxLength="50"
                  type="email"
                  uid="contact-email"
                  data-cy="contact-email"
                  v-model="dealer.profile.contactPoint.email" />
              </m-form-row>

              <m-form-row :label="t('shop-contact-name')">
                <m-text-field
                  :placeholder="t('shop-contact-name')"
                  :rules="rules.name"
                  maxLength="100"
                  uid="shop-contact-name"
                  data-cy="shop-contact-name"
                  v-model="dealer.profile.shop.contactPoint.name" />
              </m-form-row>

              <m-form-row :label="t('shop-contact-email')">
                <m-text-field
                  :placeholder="t('shop-contact-email')"
                  :rules="rules.email"
                  maxLength="50"
                  type="email"
                  uid="shop-contact-email"
                  data-cy="shop-contact-email"
                  v-model="dealer.profile.shop.contactPoint.email" />
              </m-form-row>

              <m-form-row :label="t('honda-mapit-email')">
                <m-text-field
                  :disabled="true"
                  :placeholder="t('honda-mapit-email')"
                  type="email"
                  uid="honda-mapit-email"
                  data-cy="honda-mapit-email"
                  v-model="dealer.profile.hondaMapitAccount" />
              </m-form-row>
            </expansion-panel>

            <expansion-panel :title="t('shop-data')" data-cy="ep-shop-panel">
              <location-form-group
                :dealer-country="dealer.country"
                translation-prefix="honda.repair.repairshop.shop"
                v-model="dealer.profile.shop" />
            </expansion-panel>

            <expansion-panel
              v-if="permissions.salesAppointments.canSee"
              :title="t('sales-appointment-data')"
              data-cy="ep-sales-appointment-panel">
              <sales-appointment-settings :users="dealerUsers" v-model="dealer.profile.salesAppointmentConditions" />
            </expansion-panel>

            <expansion-panel :title="t('garage-data')" data-cy="ep-garage-panel">
              <location-form-group
                :dealer-country="dealer.country"
                translation-prefix="honda.repair.repairshop.garage"
                v-model="dealer.profile.garage" />
            </expansion-panel>

            <expansion-panel
              v-if="permissions.appointments.canSee"
              :title="t('appointment-data')"
              data-cy="ep-appointment-panel">
              <m-form-row :label="t('appointment-days-before-request')">
                <m-text-field
                  :rules="rules.marginDays"
                  type="number"
                  uid="appointment-days-before-request"
                  id="appointment-days-before-request"
                  v-model="dealer.profile.appointmentConditions.daysBeforeRequest" />
              </m-form-row>

              <m-form-row>
                <template v-slot:custom-label>
                  <div>
                    <p class="mb-1">{{ t('appointments-capacity-per-slot') }}</p>
                    <small class="text--secondary d-block">
                      {{ t('appointments-capacity-per-slot-hint') }}
                    </small>
                  </div>
                </template>
                <template v-slot:default>
                  <m-text-field
                    :rules="rules.appointmentsCapacity"
                    type="number"
                    uid="appointments-capacity-per-slot"
                    id="appointments-capacity-per-slot"
                    :placeholder="'1'"
                    v-model="dealer.profile.appointmentConditions.appointmentsCapacityPerSlot" />
                </template>
              </m-form-row>

              <m-form-row :label="t('appointment-days-before-request-courtesy-bike')">
                <m-text-field
                  :rules="rules.marginDays"
                  type="number"
                  uid="appointment-days-before-request-with-courtesy-bike"
                  id="appointment-days-before-request-with-courtesy-bike"
                  v-model="dealer.profile.appointmentConditions.daysBeforeRequestWithCourtesyBike" />
              </m-form-row>

              <m-form-row>
                <template v-slot:custom-label>
                  <p class="align-self-start">
                    {{ t('appointment-holidays') }}
                  </p>
                </template>

                <template v-slot:default>
                  <v-date-picker
                    :allowed-dates="allowedDates"
                    :first-day-of-week="1"
                    :locale="locale"
                    :max="rules.dates.max"
                    :min="rules.dates.min"
                    :no-title="true"
                    class="appointments-date-picker"
                    elevation="5"
                    full-width
                    multiple
                    v-model="holidays" />
                </template>
              </m-form-row>
            </expansion-panel>
          </v-expansion-panels>

          <v-col class="col-12 text-right">
            <v-btn class="btn-gray mr-4" :disabled="loading" @click="handleClose">
              {{ $t('buttons.close') }}
            </v-btn>

            <v-btn class="btn-primary" :loading="loading" @click="handleSubmit">
              {{ $t('buttons.save') }}
            </v-btn>
          </v-col>
        </div>
      </v-form>
    </card-wrapper>
  </div>
</template>

<style global>
.appointments-date-picker {
  max-width: 360px !important;
}

.v-date-picker-header {
  padding: 1.6em 1.2em !important;
}

.v-date-picker-table {
  padding: 0 1em 2em !important;
}
</style>
